import { useState, useCallback, useEffect, useRef } from 'react'
import { clsx } from 'clsx'
import { SUIT_SYMBOL, isRed, fmtCoins } from '../../lib/cards'
import { useCountdown } from '../../hooks/useCountdown'
import type { RoundState, GameConfig, Card } from '../../lib/types'

// ── Mini card component (MAC88 style) ──────────────────────
function MiniCard({ card, faceDown }: { card?: Card; faceDown?: boolean }) {
  if (faceDown || !card) {
    return (
      <div style={{
        width: 36, height: 50, borderRadius: 4,
        background: 'linear-gradient(135deg, #1a3a6e, #0f2044)',
        border: '1px solid rgba(255,255,255,0.2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '2px 3px 8px rgba(0,0,0,0.5)',
        flexShrink: 0,
      }} />
    )
  }
  const red   = isRed(card.suit)
  const color = red ? '#dc2626' : '#111827'
  return (
    <div style={{
      width: 36, height: 50, borderRadius: 4,
      background: '#fff',
      border: '1px solid #e5e7eb',
      boxShadow: '2px 3px 8px rgba(0,0,0,0.4)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      gap: 1, flexShrink: 0, position: 'relative',
      animation: 'mac88CardIn 0.3s ease-out both',
    }}>
      <span style={{ fontSize: 14, fontWeight: 800, lineHeight: 1, color }}>{card.rank}</span>
      <span style={{ fontSize: 16, lineHeight: 1, color }}>{SUIT_SYMBOL[card.suit]}</span>
    </div>
  )
}

// ── Timer boxes (MAC88 style — two big digit squares) ──────
function TimerBoxes({ targetMs, totalMs }: { targetMs: number; totalMs: number }) {
  const remaining = useCountdown(targetMs)
  const d1        = Math.floor(remaining / 10)
  const d2        = remaining % 10
  const urgent    = remaining <= 5

  const boxStyle = (digit: number): React.CSSProperties => ({
    width: 48, height: 48,
    background: urgent ? '#dc2626' : '#1c2a3a',
    border: `2px solid ${urgent ? '#ef4444' : 'rgba(255,255,255,0.15)'}`,
    borderRadius: 6,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 28, fontWeight: 800, color: 'white',
    fontFamily: 'monospace',
    boxShadow: urgent ? '0 0 12px rgba(220,38,38,0.6)' : '0 2px 8px rgba(0,0,0,0.4)',
    transition: 'background 0.3s, border-color 0.3s',
  })

  return (
    <div style={{ display: 'flex', gap: 4 }}>
      <div style={boxStyle(d1)}>{d1}</div>
      <div style={boxStyle(d2)}>{d2}</div>
    </div>
  )
}

// ── Casino background (simulated live dealer) ──────────────
function CasinoBackground({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      position: 'relative', width: '100%', flex: 1,
      minHeight: 0, overflow: 'hidden',
      background: `
        radial-gradient(ellipse 120% 80% at 70% 50%,
          #2d1b4e 0%, #1a0f2e 30%, #0f0820 60%, #050310 100%)
      `,
    }}>
      {/* Geometric light beams */}
      <div style={{
        position: 'absolute', inset: 0,
        background: `
          linear-gradient(125deg, transparent 30%, rgba(100,60,180,0.12) 50%, transparent 70%),
          linear-gradient(245deg, transparent 20%, rgba(60,100,200,0.08) 50%, transparent 80%)
        `,
        pointerEvents: 'none',
      }} />

      {/* Diamond pattern background */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.06,
        backgroundImage: `
          repeating-linear-gradient(45deg, #c9a84c 0px, #c9a84c 1px, transparent 1px, transparent 24px),
          repeating-linear-gradient(-45deg, #c9a84c 0px, #c9a84c 1px, transparent 1px, transparent 24px)
        `,
        backgroundSize: '24px 24px',
        pointerEvents: 'none',
      }} />

      {/* Casino table surface (bottom half) */}
      <div style={{
        position: 'absolute', bottom: 0, left: '30%', right: 0, height: '55%',
        background: 'radial-gradient(ellipse 100% 80% at 50% 100%, #8b1a1a 0%, #5c1010 50%, transparent 100%)',
        opacity: 0.7,
        pointerEvents: 'none',
      }} />

      {/* Dealer silhouette area */}
      <div style={{
        position: 'absolute', right: '8%', top: '5%', bottom: '30%', width: '35%',
        background: `
          radial-gradient(ellipse 60% 90% at 50% 80%,
            rgba(139,26,26,0.3) 0%, transparent 70%)
        `,
        pointerEvents: 'none',
      }} />

      {/* Dealer figure (SVG silhouette) */}
      <svg
        style={{ position: 'absolute', right: '10%', bottom: '20%', height: '70%', opacity: 0.15 }}
        viewBox="0 0 200 400" fill="none"
      >
        {/* Head */}
        <ellipse cx="100" cy="60" rx="35" ry="42" fill="#c9a84c" />
        {/* Neck */}
        <rect x="88" y="95" width="24" height="20" fill="#c9a84c" />
        {/* Shoulders & body */}
        <path d="M30 120 Q50 110 100 115 Q150 110 170 120 L185 280 Q150 300 100 305 Q50 300 15 280 Z" fill="#8b1a1a" />
        {/* Arms */}
        <path d="M30 130 Q10 180 20 250 Q35 260 50 240 Q45 180 60 140 Z" fill="#8b1a1a" />
        <path d="M170 130 Q190 180 180 250 Q165 260 150 240 Q155 180 140 140 Z" fill="#8b1a1a" />
        {/* Hands */}
        <ellipse cx="25" cy="255" rx="18" ry="14" fill="#c9a84c" />
        <ellipse cx="175" cy="255" rx="18" ry="14" fill="#c9a84c" />
        {/* Card in hand */}
        <rect x="160" y="235" width="28" height="38" rx="3" fill="white" opacity="0.8" />
      </svg>

      {/* Spotlight effect */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(ellipse 60% 60% at 65% 40%, rgba(255,255,255,0.04) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      {/* Game watermark */}
      <div style={{
        position: 'absolute', left: '35%', top: '45%',
        transform: 'translate(-50%, -50%)',
        background: 'rgba(0,0,0,0.5)',
        border: '1px solid rgba(201,168,76,0.4)',
        borderRadius: 8, padding: '6px 14px',
        fontSize: 10, color: 'rgba(201,168,76,0.8)',
        fontWeight: 600, letterSpacing: 2,
        fontFamily: 'monospace',
        whiteSpace: 'nowrap',
      }}>
        VIRTUAL TEEN PATTI · LIVE
      </div>

      {children}
    </div>
  )
}

// ── Main component ─────────────────────────────────────────
interface Props {
  round:    RoundState
  config:   GameConfig
  placeBet: (betType: string, stake: number) => Promise<{ balance: number }>
  balance:  number | null
  displayName: string
  initialResults?: string[]
}

const CHIP_VALS = [5, 10, 25, 50, 100, 500]
const CHIP_COLORS = ['#dc2626','#2563eb','#7c3aed','#d97706','#059669','#db2777']

export function TeenPatti2Card({ round, config, placeBet, balance, displayName, initialResults }: Props) {
  const [stake,       setStake]       = useState(0)
  const [selectedChip,setSelectedChip]= useState(10)
  const [activeBet,   setActiveBet]   = useState<string | null>(null)
  const [placing,     setPlacing]     = useState(false)
  const [myBets,      setMyBets]      = useState<{ bet: string; stake: number; odds: number; outcome?: string; payout?: number }[]>([])
  const [results,     setResults]     = useState<string[]>(initialResults ?? [])

useEffect(() => {
  if (initialResults && initialResults.length > 0) {
    setResults(initialResults)
  }
}, [initialResults])

  const [toast,       setToast]       = useState<string | null>(null)
  const prevStatus = useRef(round.status)

  // Track results
  useEffect(() => {
    if (round.status === 'result' && round.result?.winner) {
      setResults(prev => [round.result!.winner!, ...prev].slice(0, 12))
    }
  }, [round.status])

  // Settle my bets when result comes
  useEffect(() => {
    if (round.status === 'result' && round.myBets.length) {
      setMyBets(round.myBets.map(b => ({
        bet:     b.betType,
        stake:   b.stake,
        odds:    config.bet_types[b.betType]?.odds ?? 0,
        outcome: b.outcome,
        payout:  b.payout,
      })))
    }
    if (round.status === 'betting' && prevStatus.current !== 'betting') {
      setMyBets([])
      setStake(0)
      setActiveBet(null)
    }
    prevStatus.current = round.status
  }, [round.status, round.myBets])

  const isBetting = round.status === 'betting'
  const isLocked  = !isBetting

  const showToast = (msg: string) => {
    setToast(msg)
    setTimeout(() => setToast(null), 2500)
  }

  const handleBetClick = (betKey: string) => {
    if (isLocked) return
    setActiveBet(betKey)
  }

  const confirmBet = useCallback(async () => {
    if (!activeBet || stake === 0 || isLocked) return
    setPlacing(true)
    try {
      await placeBet(activeBet, stake)
      setMyBets(prev => [...prev, {
        bet: activeBet, stake,
        odds: config.bet_types[activeBet]?.odds ?? 0,
      }])
      showToast(`Bet placed: ${config.bet_types[activeBet]?.label} — ${fmtCoins(stake)} coins`)
      setStake(0)
      setActiveBet(null)
    } catch (err: any) { showToast(err.message) }
    setPlacing(false)
  }, [activeBet, stake, isLocked, placeBet, config])

  const deal      = round.deal as any
  const cardsA    = round.status === 'result' ? (deal?.playerA ?? []) : []
  const cardsB    = round.status === 'result' ? (deal?.playerB ?? []) : []
  const showCards = round.status === 'result'
  const winner    = round.result?.winner

  const betStake = (key: string) =>
    myBets.filter(b => b.bet === key).reduce((s, b) => s + b.stake, 0)

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      background: '#1a1f2e', fontFamily: "'DM Sans', system-ui, sans-serif",
      color: '#e2e8f0', fontSize: 13,
    }}>

      {/* ── Header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 14px',
        background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 8, height: 8, borderRadius: '50%',
            background: isBetting ? '#22c55e' : '#f59e0b',
            boxShadow: isBetting ? '0 0 8px #22c55e' : '0 0 8px #f59e0b',
            animation: 'pulse 2s infinite',
          }} />
          <span style={{ fontWeight: 700, fontSize: 15, letterSpacing: 0.5 }}>
            TEEN PATTI 2-CARD
          </span>
          <span style={{
            fontSize: 10, color: '#64748b', padding: '2px 8px',
            background: 'rgba(255,255,255,0.05)', borderRadius: 20,
          }}>Rules</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {round.roundId && (
            <span style={{ fontSize: 11, color: '#475569' }}>
              Round ID: {round.roundId.slice(0,16).toUpperCase()}
            </span>
          )}
          <span style={{ fontSize: 11, color: '#3b82f6', cursor: 'pointer' }}>
            Player History
          </span>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)',
            borderRadius: 8, padding: '4px 10px',
          }}>
            <span style={{ fontSize: 10, color: 'rgba(201,168,76,0.6)' }}>BAL</span>
            <span style={{ fontWeight: 700, color: '#c9a84c', fontSize: 14 }}>
              {balance !== null ? `₹${fmtCoins(balance)}` : '—'}
            </span>
          </div>
        </div>
      </div>

      {/* ── Main body ── */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>

        {/* ── Left: Video + Cards + Betting ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>

          {/* ── Casino "Video" Area ── */}
          <CasinoBackground>
            {/* Player A cards overlay — top left */}
            <div style={{
              position: 'absolute', top: 12, left: 12,
              background: 'rgba(0,0,0,0.7)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 6, padding: '8px 12px',
              backdropFilter: 'blur(4px)',
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 1, marginBottom: 8 }}>
                PLAYER A {winner === 'A' && <span style={{ color: '#22c55e', marginLeft: 4 }}>✓ WIN</span>}
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {showCards
                  ? cardsA.map((c: Card, i: number) => <MiniCard key={i} card={c} />)
                  : [0,1].map(i => <MiniCard key={i} faceDown />)
                }
              </div>
              {round.result?.sumA !== undefined && (
                <div style={{ marginTop: 6, fontSize: 10, color: '#94a3b8' }}>
                  Sum: <b style={{ color: 'white' }}>{round.result.sumA}</b>
                  {'  '}Bac: <b style={{ color: '#c9a84c' }}>{round.result.bacA}</b>
                </div>
              )}
            </div>

            {/* Player B cards overlay */}
            <div style={{
              position: 'absolute', top: 80, left: 12,
              background: 'rgba(0,0,0,0.7)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 6, padding: '8px 12px',
              backdropFilter: 'blur(4px)',
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: 1, marginBottom: 8 }}>
                PLAYER B {winner === 'B' && <span style={{ color: '#22c55e', marginLeft: 4 }}>✓ WIN</span>}
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {showCards
                  ? cardsB.map((c: Card, i: number) => <MiniCard key={i} card={c} />)
                  : [0,1].map(i => <MiniCard key={i} faceDown />)
                }
              </div>
              {round.result?.sumB !== undefined && (
                <div style={{ marginTop: 6, fontSize: 10, color: '#94a3b8' }}>
                  Sum: <b style={{ color: 'white' }}>{round.result.sumB}</b>
                  {'  '}Bac: <b style={{ color: '#c9a84c' }}>{round.result.bacB}</b>
                </div>
              )}
            </div>

            {/* Status badge */}
            <div style={{
              position: 'absolute', bottom: 12, left: 12,
              fontSize: 11, fontWeight: 700, letterSpacing: 2,
              color: isBetting ? '#22c55e' : '#f59e0b',
              textTransform: 'uppercase',
              textShadow: isBetting ? '0 0 12px #22c55e' : '0 0 12px #f59e0b',
            }}>
              {round.status === 'idle'    ? 'Waiting for round…'
               : round.status === 'betting' ? '♠ Place Your Bets'
               : round.status === 'closed'  ? 'Dealing cards…'
               : `Player ${winner === 'D' ? '— Draw' : `${winner} Wins!`}`}
            </div>

            {/* Timer — top right of video */}
            {isBetting && (
              <div style={{ position: 'absolute', top: 12, right: 12 }}>
                <TimerBoxes targetMs={round.bettingCloses} totalMs={config.betting_window_ms} />
              </div>
            )}

            {/* Win overlay */}
            {round.status === 'result' && round.winAmount > 0 && (
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)',
                animation: 'mac88WinIn 0.4s ease-out',
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: 36, fontWeight: 900,
                    background: 'linear-gradient(90deg, #c9a84c, #f0e68c, #c9a84c)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    letterSpacing: 2, fontFamily: "'Playfair Display', serif",
                  }}>YOU WIN!</div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: '#c9a84c', marginTop: 4 }}>
                    +{fmtCoins(round.winAmount)} coins
                  </div>
                </div>
              </div>
            )}
          </CasinoBackground>

          {/* ── Betting Panel (MAC88 style) ── */}
          <div style={{
            background: '#0f172a',
            borderTop: '2px solid #1e293b',
            flexShrink: 0,
          }}>
            {/* Column headers */}
            <div style={{ display: 'flex', borderBottom: '1px solid #1e293b' }}>
              <div style={{ flex: 1, padding: '6px 12px', fontSize: 12, fontWeight: 600, color: '#94a3b8' }}>
                Player A
              </div>
              <div style={{ display: 'flex', minWidth: 180 }}>
                <div style={{ flex: 1, padding: '6px 0', textAlign: 'center', background: '#0ea5e9', fontSize: 11, fontWeight: 700, color: 'white' }}>Back</div>
                <div style={{ flex: 1, padding: '6px 0', textAlign: 'center', background: '#f43f5e', fontSize: 11, fontWeight: 700, color: 'white' }}>Lay</div>
              </div>
              <div style={{ flex: 1, padding: '6px 12px', fontSize: 12, fontWeight: 600, color: '#94a3b8' }}>
                Player B
              </div>
              <div style={{ display: 'flex', minWidth: 180 }}>
                <div style={{ flex: 1, padding: '6px 0', textAlign: 'center', background: '#0ea5e9', fontSize: 11, fontWeight: 700, color: 'white' }}>Back</div>
                <div style={{ flex: 1, padding: '6px 0', textAlign: 'center', background: '#f43f5e', fontSize: 11, fontWeight: 700, color: 'white' }}>Lay</div>
              </div>
            </div>

            {/* Main bet row */}
            <BetRow
              labelA="Main" labelB="Main"
              oddsA={config.bet_types['player_a']?.odds ?? 1.98}
              oddsB={config.bet_types['player_b']?.odds ?? 1.98}
              stakeA={betStake('player_a')} stakeB={betStake('player_b')}
              locked={isLocked}
              activeA={activeBet === 'player_a'} activeB={activeBet === 'player_b'}
              onClickA={() => handleBetClick('player_a')}
              onClickB={() => handleBetClick('player_b')}
            />

            {/* Mini Bac row */}
            <BetRow
              labelA="Mini Bac" labelB="Mini Bac"
              oddsA={config.bet_types['mini_bac_a']?.odds ?? 2.1}
              oddsB={config.bet_types['mini_bac_b']?.odds ?? 2.1}
              stakeA={betStake('mini_bac_a')} stakeB={betStake('mini_bac_b')}
              locked={isLocked}
              activeA={activeBet === 'mini_bac_a'} activeB={activeBet === 'mini_bac_b'}
              onClickA={() => handleBetClick('mini_bac_a')}
              onClickB={() => handleBetClick('mini_bac_b')}
            />

            {/* Color Plus + Under/Over */}
            <div style={{ display: 'flex', borderTop: '1px solid #1e293b' }}>
              {/* Color Plus */}
              <button
                onClick={() => handleBetClick('color_plus')}
                disabled={isLocked}
                style={{
                  flex: 1, padding: '8px 12px', textAlign: 'left', cursor: isLocked ? 'default' : 'pointer',
                  background: activeBet === 'color_plus' ? 'rgba(201,168,76,0.15)' : 'transparent',
                  border: 'none', color: '#c9a84c', fontSize: 12, fontWeight: 600,
                  borderRight: '1px solid #1e293b',
                }}>
                Color Plus ×{config.bet_types['color_plus']?.odds ?? 3}
                {betStake('color_plus') > 0 && (
                  <span style={{ marginLeft: 8, fontSize: 10, color: '#22c55e' }}>
                    {fmtCoins(betStake('color_plus'))}
                  </span>
                )}
              </button>

              {/* Under/Over grid */}
              <div style={{ display: 'flex', flex: 1, gap: 1, background: '#1e293b' }}>
                {[
                  { key: 'total_a_under', label: 'A U', odds: 14 },
                  { key: 'total_a_over',  label: 'A O', odds: 15 },
                  { key: 'total_b_under', label: 'B U', odds: 14 },
                  { key: 'total_b_over',  label: 'B O', odds: 15 },
                ].map(b => (
                  <button key={b.key}
                    onClick={() => handleBetClick(b.key)}
                    disabled={isLocked}
                    style={{
                      flex: 1, padding: '6px 4px', cursor: isLocked ? 'default' : 'pointer',
                      background: activeBet === b.key ? 'rgba(59,130,246,0.3)' : '#0f172a',
                      border: 'none', color: '#e2e8f0', fontSize: 10, fontWeight: 600,
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                    }}>
                    <span style={{ color: '#94a3b8' }}>{b.label}</span>
                    <span style={{ color: '#c9a84c' }}>×{config.bet_types[b.key]?.odds ?? b.odds}</span>
                    {betStake(b.key) > 0 && (
                      <span style={{ fontSize: 9, color: '#22c55e' }}>{fmtCoins(betStake(b.key))}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Chip selector + Place Bet */}
            {activeBet && !isLocked && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', borderTop: '1px solid #1e293b',
                background: 'rgba(59,130,246,0.05)',
                animation: 'mac88SlideIn 0.2s ease-out',
              }}>
                <span style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>
                  {config.bet_types[activeBet]?.label}:
                </span>
                <div style={{ display: 'flex', gap: 4, flex: 1 }}>
                  {CHIP_VALS.map((v, i) => (
                    <button key={v} onClick={() => { setSelectedChip(v); setStake(s => s + v) }}
                      style={{
                        width: 36, height: 36, borderRadius: '50%',
                        background: `radial-gradient(circle at 35% 35%, ${CHIP_COLORS[i]}cc, ${CHIP_COLORS[i]}88)`,
                        border: selectedChip === v ? '2px solid white' : '2px solid rgba(255,255,255,0.2)',
                        color: 'white', fontSize: 10, fontWeight: 700, cursor: 'pointer',
                        flexShrink: 0,
                      }}>
                      {v >= 1000 ? `${v/1000}k` : v}
                    </button>
                  ))}
                </div>
                <div style={{
                  padding: '4px 10px', background: 'rgba(201,168,76,0.1)',
                  border: '1px solid rgba(201,168,76,0.3)', borderRadius: 6,
                  color: '#c9a84c', fontWeight: 700, minWidth: 60, textAlign: 'center',
                  fontSize: 13,
                }}>
                  {stake > 0 ? fmtCoins(stake) : '—'}
                </div>
                {stake > 0 && (
                  <button onClick={() => setStake(0)} style={{
                    padding: '4px 8px', background: 'rgba(220,38,38,0.2)',
                    border: '1px solid rgba(220,38,38,0.3)', borderRadius: 4,
                    color: '#f87171', fontSize: 11, cursor: 'pointer',
                  }}>CLR</button>
                )}
                <button
                  onClick={confirmBet}
                  disabled={placing || stake === 0}
                  style={{
                    padding: '6px 16px', background: stake > 0 ? '#16a34a' : '#1e293b',
                    border: 'none', borderRadius: 6, color: 'white', fontSize: 12,
                    fontWeight: 700, cursor: stake > 0 ? 'pointer' : 'default',
                    opacity: placing ? 0.6 : 1, whiteSpace: 'nowrap',
                  }}>
                  {placing ? 'Placing…' : 'Place Bet'}
                </button>
              </div>
            )}

            {/* Toast */}
            {toast && (
              <div style={{
                padding: '6px 14px', background: '#166534',
                borderTop: '1px solid #15803d',
                fontSize: 12, color: '#86efac',
                animation: 'mac88SlideIn 0.2s ease-out',
              }}>
                {toast}
              </div>
            )}
          </div>

          {/* ── Last Result bar ── */}
          <div style={{
            display: 'flex', alignItems: 'center',
            padding: '6px 12px', gap: 12,
            background: '#0f172a',
            borderTop: '1px solid #1e293b',
            flexShrink: 0,
          }}>
            <span style={{ fontSize: 11, color: '#475569', whiteSpace: 'nowrap', fontWeight: 600 }}>
              Last Result
            </span>
            <div style={{ display: 'flex', gap: 5, flex: 1, overflow: 'hidden' }}>
              {results.length === 0 && (
                <span style={{ fontSize: 11, color: '#334155' }}>No results yet</span>
              )}
              {results.map((r, i) => (
                <div key={i} style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: r === 'A' ? '#c84b31' : r === 'B' ? '#1d6b35' : '#475569',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 800, color: 'white',
                  border: '2px solid rgba(255,255,255,0.15)',
                  flexShrink: 0,
                }}>
                  {r}
                </div>
              ))}
            </div>
            <span style={{ fontSize: 11, color: '#3b82f6', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              View All
            </span>
          </div>
        </div>

        {/* ── Right: My Bet panel ── */}
        <div style={{
          width: 220, flexShrink: 0, display: 'flex', flexDirection: 'column',
          borderLeft: '1px solid #1e293b',
          background: '#0f172a',
        }}>
          {/* My Bet header */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '8px 12px',
            borderBottom: '1px solid #1e293b',
            background: 'linear-gradient(180deg, #1e293b, #0f172a)',
          }}>
            <span style={{ fontWeight: 700, fontSize: 13 }}>My Bet</span>
          </div>

          {/* Column headers */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr auto auto',
            gap: 4, padding: '5px 10px',
            borderBottom: '1px solid #1e293b',
            fontSize: 10, color: '#475569', fontWeight: 600,
          }}>
            <span>Matched Bet</span>
            <span>Odds</span>
            <span>Stake</span>
          </div>

          {/* Bet list */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {myBets.length === 0 ? (
              <div style={{
                padding: '20px 12px', textAlign: 'center',
                fontSize: 11, color: '#334155',
              }}>
                No bets placed yet
              </div>
            ) : myBets.map((b, i) => (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: '1fr auto auto',
                gap: 4, padding: '6px 10px',
                borderBottom: '1px solid rgba(255,255,255,0.04)',
                background: b.outcome === 'win' ? 'rgba(34,197,94,0.06)'
                  : b.outcome === 'loss' ? 'rgba(239,68,68,0.06)' : 'transparent',
                fontSize: 11,
              }}>
                <div>
                  <div style={{ fontWeight: 600, color: '#e2e8f0', fontSize: 10 }}>
                    {config.bet_types[b.bet]?.label ?? b.bet}
                  </div>
                  {b.outcome && (
                    <div style={{
                      fontSize: 9, fontWeight: 700, marginTop: 2,
                      color: b.outcome === 'win' ? '#22c55e' : b.outcome === 'push' ? '#f59e0b' : '#ef4444',
                    }}>
                      {b.outcome === 'win' ? `+${fmtCoins(b.payout ?? 0)}` : b.outcome.toUpperCase()}
                    </div>
                  )}
                </div>
                <span style={{ color: '#0ea5e9', fontWeight: 700 }}>{b.odds}</span>
                <span style={{ color: '#94a3b8' }}>{fmtCoins(b.stake)}</span>
              </div>
            ))}
          </div>

          {/* Player info */}
          <div style={{
            padding: '8px 12px',
            borderTop: '1px solid #1e293b',
            background: 'rgba(255,255,255,0.02)',
          }}>
            <div style={{ fontSize: 10, color: '#475569' }}>Player</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8' }}>{displayName}</div>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes mac88CardIn {
          from { opacity: 0; transform: translateY(-16px) scale(0.9); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes mac88WinIn {
          from { opacity: 0; transform: scale(0.9); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes mac88SlideIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.4; }
        }
      `}</style>
    </div>
  )
}

// ── Bet row component ──────────────────────────────────────
function BetRow({
  labelA, labelB, oddsA, oddsB, stakeA, stakeB,
  locked, activeA, activeB, onClickA, onClickB,
}: {
  labelA: string; labelB: string
  oddsA: number; oddsB: number
  stakeA: number; stakeB: number
  locked: boolean
  activeA: boolean; activeB: boolean
  onClickA: () => void; onClickB: () => void
}) {
  const cellStyle = (active: boolean, color: string): React.CSSProperties => ({
    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', padding: '7px 4px', cursor: locked ? 'default' : 'pointer',
    background: active ? `${color}33` : locked ? 'rgba(255,255,255,0.02)' : `${color}18`,
    border: 'none',
    borderLeft: '1px solid #1e293b',
    transition: 'background 0.15s',
    minWidth: 0,
  })

  const LockIcon = () => (
    <svg width="12" height="14" viewBox="0 0 12 14" fill="none">
      <rect x="2" y="6" width="8" height="7" rx="1.5" fill="#475569"/>
      <path d="M3.5 6V4.5a2.5 2.5 0 015 0V6" stroke="#475569" strokeWidth="1.5"/>
    </svg>
  )

  return (
    <div style={{ display: 'flex', borderTop: '1px solid #1e293b' }}>
      {/* Label A */}
      <div style={{ flex: 1, padding: '7px 12px', display: 'flex', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: '#94a3b8' }}>{labelA}</span>
        {stakeA > 0 && (
          <span style={{ marginLeft: 6, fontSize: 10, color: '#22c55e', fontWeight: 700 }}>
            {fmtCoins(stakeA)}
          </span>
        )}
      </div>
      {/* Back A */}
      <button style={cellStyle(activeA, '#0ea5e9')} onClick={onClickA} disabled={locked}>
        {locked ? <LockIcon /> : (
          <>
            <span style={{ fontSize: 13, fontWeight: 800, color: '#7dd3fc' }}>{oddsA}</span>
            <span style={{ fontSize: 9, color: '#475569' }}>0</span>
          </>
        )}
      </button>
      {/* Lay A */}
      <button style={cellStyle(false, '#f43f5e')} disabled>
        {locked ? <LockIcon /> : (
          <>
            <span style={{ fontSize: 13, fontWeight: 800, color: '#fca5a5' }}>{oddsA}</span>
            <span style={{ fontSize: 9, color: '#475569' }}>0</span>
          </>
        )}
      </button>
      {/* Label B */}
      <div style={{ flex: 1, padding: '7px 12px', display: 'flex', alignItems: 'center', borderLeft: '2px solid #1e293b' }}>
        <span style={{ fontSize: 12, color: '#94a3b8' }}>{labelB}</span>
        {stakeB > 0 && (
          <span style={{ marginLeft: 6, fontSize: 10, color: '#22c55e', fontWeight: 700 }}>
            {fmtCoins(stakeB)}
          </span>
        )}
      </div>
      {/* Back B */}
      <button style={cellStyle(activeB, '#0ea5e9')} onClick={onClickB} disabled={locked}>
        {locked ? <LockIcon /> : (
          <>
            <span style={{ fontSize: 13, fontWeight: 800, color: '#7dd3fc' }}>{oddsB}</span>
            <span style={{ fontSize: 9, color: '#475569' }}>0</span>
          </>
        )}
      </button>
      {/* Lay B */}
      <button style={cellStyle(false, '#f43f5e')} disabled>
        {locked ? <LockIcon /> : (
          <>
            <span style={{ fontSize: 13, fontWeight: 800, color: '#fca5a5' }}>{oddsB}</span>
            <span style={{ fontSize: 9, color: '#475569' }}>0</span>
          </>
        )}
      </button>
    </div>
  )
}
